import React from 'react';
import {
  C,
  CARD_W,
  PARTICLE_TIMER_H,
  nx,
  ny,
} from '../flowCanvas.constants';

// ─── Layout constants ──────────────────────────────────────────────────────────
const HEADER_H   = 48;
const DIAL_SIZE  = 64;   // diameter of the clock face circle
const PILL_H     = 22;
const PILL_GAP   = 5;
const LABEL_X    = 12;
const TOGGLE_X   = CARD_W - LABEL_X - (CARD_W - LABEL_X - 12) + LABEL_X;  // re-used below

const PRESETS = [
  { label: '5s', seconds: 5 },
  { label: '10s', seconds: 10 },
  { label: '20s', seconds: 20 },
  { label: '30s', seconds: 30 },
  { label: '45s', seconds: 45 },
  { label: '1m', seconds: 60 },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────
function formatSeconds(value) {
  const totalSec = Math.round(Number(value) || 0);
  if (totalSec <= 0) return '—';
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function toBool(val) {
  if (val === true || val === false) return val;
  if (typeof val === 'string') {
    const l = val.trim().toLowerCase();
    if (l === 'true') return true;
    if (l === 'false') return false;
  }
  if (typeof val === 'number') return val === 1;
  return false;
}

function getGlobalSettings(step, liveCustomisationData) {
  // Reads from liveCustomisationData.particleOptions.global.settings[0]
  // and falls back to populatedData for the initial render.
  const liveArr = liveCustomisationData?.particleOptions?.global?.settings;
  if (Array.isArray(liveArr) && liveArr[0]) return liveArr[0];
  const populated = step?.populatedData?.variables?.[0]?.allValues;
  if (Array.isArray(populated) && populated[0]) return populated[0];
  return {};
}

// ─── Sub-components ────────────────────────────────────────────────────────────
function TogglePill({ x, y, width, active, label, onClick }) {
  return (
    <g onClick={(e) => { e.stopPropagation(); onClick?.(); }} style={{ cursor: 'pointer' }}>
      <rect
        x={x} y={y} width={width} height={PILL_H} rx={PILL_H / 2}
        fill={active ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)'}
        stroke={active ? 'rgba(74,222,128,0.58)' : 'rgba(145,70,255,0.22)'}
        strokeWidth="1"
      />
      <circle cx={x + (active ? width - 11 : 11)} cy={y + PILL_H / 2} r="7"
        fill={active ? C.greenText : C.textMuted} />
      <text
        x={x + width / 2} y={y + PILL_H / 2 + 3.5}
        textAnchor="middle" fontSize="9" fontWeight="700"
        fill={active ? C.greenText : C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
}

function PresetPill({ x, y, w, label, active, onClick, disabled = false }) {
  return (
    <g onClick={(e) => { e.stopPropagation(); if (!disabled) onClick?.(); }} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <rect
        x={x} y={y} width={w} height={PILL_H} rx={PILL_H / 2}
        fill={disabled ? 'rgba(255,255,255,0.03)' : active ? 'rgba(145,70,255,0.22)' : 'rgba(255,255,255,0.04)'}
        stroke={disabled ? 'rgba(136,146,176,0.16)' : active ? C.purple : 'rgba(145,70,255,0.20)'}
        strokeWidth={active ? 1.5 : 1}
      />
      <text
        x={x + w / 2} y={y + PILL_H / 2 + 3.5}
        textAnchor="middle" fontSize="9" fontWeight={active ? '800' : '600'}
        fill={disabled ? C.textDim : active ? C.purple : C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    </g>
  );
}

// ─── Main Node ─────────────────────────────────────────────────────────────────
export default function ParticleTimerNode({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onFieldChange,
}) {
  const x    = overrideX ?? nx(step);
  const y    = overrideY ?? ny(step, centreY);
  const left = x - CARD_W / 2;
  const top  = y - PARTICLE_TIMER_H / 2;

  const settings  = getGlobalSettings(step, liveCustomisationData);
  const liveTimeRaw = settings?.liveTime;
  const liveTime  = Number.isFinite(Number(liveTimeRaw)) ? Number(liveTimeRaw) : 30;
  const autoEnd   = toBool(settings.autoEnd);
  const isManualStop = !autoEnd;
  const timeLabel = formatSeconds(liveTime);
  const secondsInMinute = ((Math.round(liveTime) % 60) + 60) % 60;
  const effectLabel = step?.stepId?.startsWith('fw_') ? 'Fireworks' : 'Confetti';

  // ── Preset pill row layout ──────────────────────────────────────────────────
  const pillRowY = top + HEADER_H + DIAL_SIZE + 48;
  const innerW   = CARD_W - LABEL_X * 2;
  const pillW    = Math.floor((innerW - PILL_GAP * (PRESETS.length - 1)) / PRESETS.length);

  // ── Auto-end toggle ─────────────────────────────────────────────────────────
  const toggleRowY  = pillRowY + PILL_H + 22;
  const toggleW     = CARD_W - LABEL_X * 2;

  // ── Clock dial ──────────────────────────────────────────────────────────────
  const dialCX = left + CARD_W / 2;
  const dialCY = top + HEADER_H + DIAL_SIZE / 2 + 16;
  const dialR  = DIAL_SIZE / 2;

  return (
    <g className="fc-node fc-node--particle-timer" onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Drop shadow */}
      <rect x={left + 2} y={top + 4} width={CARD_W} height={PARTICLE_TIMER_H} rx="10"
        fill="#000" fillOpacity="0.3" />

      {/* Card background */}
      <rect
        x={left} y={top} width={CARD_W} height={PARTICLE_TIMER_H} rx="10"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.purple}
        strokeWidth={selected ? 2 : 1.5}
      />

      {/* Header divider */}
      <line x1={left + 1} y1={top + HEADER_H} x2={left + CARD_W - 1} y2={top + HEADER_H}
        stroke={C.border} strokeWidth="1" />

      {/* Title */}
      <text x={left + 14} y={top + 25} fontSize="11.5" fontWeight="700" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {step.stepTitle || 'Duration'}
      </text>
      <text x={left + 14} y={top + 39} fontSize="8.5" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        How long particles run
      </text>

      {/* ── Clock dial ────────────────────────────────────────────────────────── */}
      {/* Outer ring */}
      <circle cx={dialCX} cy={dialCY} r={dialR}
        fill={isManualStop ? 'rgba(255,255,255,0.03)' : 'rgba(145,70,255,0.06)'}
        stroke={isManualStop ? 'rgba(136,146,176,0.22)' : C.purple}
        strokeWidth="1.5" />
      {/* Inner tick marks */}
      {Array.from({ length: 12 }, (_, i) => {
        const angle = (i * 30 - 90) * (Math.PI / 180);
        const inner = dialR - 6;
        const outer = dialR - 2;
        return (
          <line
            key={i}
            x1={dialCX + inner * Math.cos(angle)}
            y1={dialCY + inner * Math.sin(angle)}
            x2={dialCX + outer * Math.cos(angle)}
            y2={dialCY + outer * Math.sin(angle)}
            stroke={isManualStop ? 'rgba(136,146,176,0.18)' : i % 3 === 0 ? C.purple : 'rgba(145,70,255,0.30)'}
            strokeWidth={i % 3 === 0 ? 1.5 : 1}
          />
        );
      })}
      {/* Clock-hand (represents the current minute cycle, safe for values > 1 minute) */}
      {(() => {
        const angle  = ((secondsInMinute / 60) * 360 - 90) * (Math.PI / 180);
        const handR  = dialR - 9;
        return (
          <line
            x1={dialCX} y1={dialCY}
            x2={dialCX + handR * Math.cos(angle)}
            y2={dialCY + handR * Math.sin(angle)}
            stroke={isManualStop ? C.textDim : C.purple} strokeWidth="2" strokeLinecap="round"
          />
        );
      })()}
      {/* Centre dot */}
      <circle cx={dialCX} cy={dialCY} r="3" fill={isManualStop ? C.textDim : C.purple} />
      {/* Time label under dial */}
      {isManualStop ? (
        <>
          <text x={dialCX} y={dialCY + dialR + 16}
            textAnchor="middle" fontSize="9.5" fontWeight="800"
            fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {effectLabel}
          </text>
          <text x={dialCX} y={dialCY + dialR + 30}
            textAnchor="middle" fontSize="9.5" fontWeight="800"
            fill={C.textMuted} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            manually stopped
          </text>
        </>
      ) : (
        <text x={dialCX} y={dialCY + dialR + 21}
          textAnchor="middle" fontSize="15" fontWeight="800"
          fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {timeLabel}
        </text>
      )}

      {/* ── Preset pills ──────────────────────────────────────────────────────── */}
      {PRESETS.map((preset, i) => (
        <PresetPill
          key={preset.label}
          x={left + LABEL_X + i * (pillW + PILL_GAP)}
          y={pillRowY}
          w={pillW}
          label={preset.label}
          active={!isManualStop && liveTime === preset.seconds}
          disabled={isManualStop}
          onClick={() => onFieldChange?.('liveTime', preset.seconds)}
        />
      ))}

      {/* ── Auto-end toggle ────────────────────────────────────────────────────── */}
      <text
        x={left + LABEL_X} y={toggleRowY + PILL_H / 2 + 3.5}
        fontSize="9" fontWeight="700" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Auto End
      </text>
      <TogglePill
        x={left + LABEL_X + 58}
        y={toggleRowY}
        width={toggleW - 58}
        active={autoEnd}
        label={autoEnd ? 'Enabled' : 'Disabled'}
        onClick={() => onFieldChange?.('autoEnd', !autoEnd)}
      />
    </g>
  );
}
